import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import ModalContainer from "components/ModalContainer";
import Form from "../../../../System/RealmSetting/components/Profiles/MacAddress/util/form";
import MainView from "./MainView";

const createEntry = () => ({
  id: `${Date.now()}-${Math.random()}`,
  ipAddress: "",
  username: "",
  password: "",
  role: "",
});
const isValidIPv4 = (value) => {
  const octets = value.split(".");
  return octets.length === 4 && octets.every((octet) => {
    if (!/^\d{1,3}$/.test(octet)) return false;
    const number = Number(octet);
    return number >= 0 && number <= 255;
  });
};

const ModalDataCenterSwitch = ({
  openModal,
  proxyDevices,
  selectedProxy,
  savedEntries,
  onCloseModal,
  onSaveProxy,
}) => {
  const theme = useTheme();
  const form = Form.init(theme);
  const [selectedProxyUuid, setSelectedProxyUuid] = useState("");
  const [entries, setEntries] = useState([createEntry()]);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);

  useEffect(() => {
    if (!openModal) return;

    setSelectedProxyUuid(
      proxyDevices.length === 1
        ? proxyDevices[0].proxyUuid
        : selectedProxy?.proxyUuid || "",
    );
    setEntries(savedEntries.length > 0 ? savedEntries : [createEntry()]);
    setSelectedEntryIds([]);
  }, [openModal, proxyDevices, selectedProxy, savedEntries]);

  const matchedProxy = proxyDevices.find(
    (device) => device.proxyUuid === selectedProxyUuid,
  );
  const canUseProxy = matchedProxy?.linkStatus === "online";
  const isEntryValid = (entry) => (
    isValidIPv4(entry.ipAddress.trim()) &&
    entry.username.trim() !== "" &&
    entry.password !== ""
  );
  const areEntriesValid = entries.length > 0 && entries.every(isEntryValid);

  const updateEntry = (id, key, value) => {
    setEntries((previous) => previous.map((entry) => (
      entry.id === id ? { ...entry, [key]: value } : entry
    )));
  };

  const toggleAllEntries = (checked) => {
    setSelectedEntryIds(checked ? entries.map((entry) => entry.id) : []);
  };

  const toggleEntry = (id) => {
    setSelectedEntryIds((previous) => (
      previous.includes(id)
        ? previous.filter((entryId) => entryId !== id)
        : [...previous, id]
    ));
  };

  const importEntries = (newEntries, replaceExisting) => {
    if (!replaceExisting) {
      addEntries(newEntries);
      setSelectedEntryIds([]);
      return;
    }

    setEntries((previous) => {
      const updatedEntries = [...previous];
      newEntries.forEach((newEntry) => {
        const matchedIndex = updatedEntries.findIndex((entry) => (
          entry.ipAddress.trim().toLowerCase() === newEntry.ipAddress.trim().toLowerCase()
        ));
        if (matchedIndex >= 0) {
          updatedEntries[matchedIndex] = {
            ...updatedEntries[matchedIndex],
            ...newEntry,
          };
        } else {
          updatedEntries.push({ ...createEntry(), ...newEntry });
        }
      });
      return updatedEntries;
    });
    setSelectedEntryIds([]);
  };

  const addEntries = (newEntries) => {
    setEntries((previous) => [
      ...previous,
      ...newEntries.map((entry) => ({ ...createEntry(), ...entry })),
    ]);
  };

  const deleteSelectedEntries = () => {
    setEntries((previous) => previous.filter((entry) => !selectedEntryIds.includes(entry.id)));
    setSelectedEntryIds([]);
  };

  const handleSave = () => {
    if (!canUseProxy || !areEntriesValid) return;
    const saved = entries.filter((entry) => (
      entry.ipAddress || entry.username || entry.password || entry.role
    ));
    onSaveProxy(matchedProxy, saved);
  };

  if (!openModal) return null;

  return (
    <ModalContainer display modalWidth="auto">
      <MainView
        form={form}
        proxyDevices={proxyDevices}
        selectedProxyUuid={selectedProxyUuid}
        setSelectedProxyUuid={setSelectedProxyUuid}
        entries={entries}
        selectedEntryIds={selectedEntryIds}
        onCloseModal={onCloseModal}
        onAddEntry={() => setEntries((previous) => [...previous, createEntry()])}
        onDeleteSelected={deleteSelectedEntries}
        onUpdateEntry={updateEntry}
        onToggleAllEntries={toggleAllEntries}
        onToggleEntry={toggleEntry}
        onUploadEntries={importEntries}
        canSave={canUseProxy && areEntriesValid}
        onSave={handleSave}
      />
    </ModalContainer>
  );
};

export default ModalDataCenterSwitch;
